import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, BrainCircuit, Glasses, Radar, GitMerge,
  Workflow, GraduationCap, Cpu, Cloud, Layers, Compass, Lightbulb,
  Timer, MessageSquare,
} from "lucide-react";
import CredibilityStrip from "@/components/CredibilityStrip";
import { Reveal, ChapterTag, MaskedLine } from "@/components/Reveal";

const chips = ["AI", "SMART GLASSES", "HARDWARE + SOFTWARE", "AUTOMATION", "IOT", "ASSET TRACKING", "EMERGING TECH"];

const capabilities = [
  {
    icon: BrainCircuit, title: "AI STRATEGY & SOLUTIONS", span: "md:col-span-2",
    desc: "AI strategy, custom AI solutions, generative AI, multimodal systems, AI agents, and computer vision — evaluated, prototyped, and implemented around your business goals.",
    tags: ["STRATEGY", "GENAI", "AGENTS", "VISION"],
  },
  {
    icon: Glasses, title: "SMART GLASS & WEARABLE DEVELOPMENT", span: "",
    desc: "Wearable application development since the original Google Glass release — dozens of apps built for the platform.",
    tags: ["GLASS", "VOICE", "CAMERA"],
  },
  {
    icon: Radar, title: "IOT & ASSET TRACKING", span: "",
    desc: "GPS, cellular, BLE, Wi-Fi, RFID, sensors, and connected devices — from field hardware to live dashboards.",
    tags: ["GPS", "BLE", "RFID"],
  },
  {
    icon: GitMerge, title: "HARDWARE + SOFTWARE INTEGRATION", span: "",
    desc: "Complete systems where physical devices, connectivity, cloud, and software work as one.",
    tags: ["EDGE", "EMBEDDED", "CLOUD"],
  },
  {
    icon: Workflow, title: "BUSINESS PROCESS AUTOMATION", span: "",
    desc: "Automation that removes repetitive work and connects the systems you already use.",
    tags: ["WORKFLOWS", "APIS"],
  },
  {
    icon: GraduationCap, title: "AI TRAINING", span: "",
    desc: "Practical AI training that gives teams working knowledge — not buzzwords.",
    tags: ["WORKSHOPS", "ENABLEMENT"],
  },
];

const differentiators = [
  { icon: Layers, title: "BOTH SIDES OF TECHNOLOGY", sub: "HARDWARE + SOFTWARE EXPERIENCE", desc: "Extensive experience combining physical hardware with software, cloud platforms, connectivity, data, automation, and AI.", span: "md:col-span-2" },
  { icon: Compass, title: "COMPLETE SYSTEM THINKING", desc: "I look at the complete technology system — device, communications, cloud, software, user experience, and business process.", span: "" },
  { icon: Cpu, title: "BROAD TECHNOLOGY EXPERIENCE", desc: "Experience across multiple generations and categories of technology.", span: "" },
  { icon: Glasses, title: "EARLY TECHNOLOGY ADOPTION", desc: "Hands-on development for emerging platforms, including Google Glass during its original release.", span: "" },
  { icon: Lightbulb, title: "PRACTICAL BUSINESS APPROACH", desc: "Technology should solve business problems — not exist simply because it is interesting.", span: "" },
  { icon: Timer, title: "RAPID PROTOTYPING", desc: "Quickly determine whether an idea works before making large investments.", span: "" },
  { icon: MessageSquare, title: "COMPLEX MADE UNDERSTANDABLE", desc: "Advanced technology explained in practical business terms.", span: "" },
];

const equationWords = ["HARDWARE", "SOFTWARE", "AI", "SMART GLASSES", "IOT", "CONNECTIVITY", "CLOUD", "AUTOMATION"];

const EquationWord = ({ word, index, total, progress }) => {
  const start = (index / (total + 1)) * 0.85;
  const end = start + 0.08;
  const opacity = useTransform(progress, [start, end], [0.08, 1]);
  const color = useTransform(progress, [start, end], ["#3a3a3a", "#ffffff"]);
  return (
    <motion.span style={{ opacity, color }} className="font-unbounded font-semibold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl">
      {word}
    </motion.span>
  );
};

const EquationSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const resultOpacity = useTransform(scrollYProgress, [0.78, 0.92], [0, 1]);
  const resultY = useTransform(scrollYProgress, [0.78, 0.92], [40, 0]);
  const lineScale = useTransform(scrollYProgress, [0.1, 0.8], [0, 1]);

  return (
    <section ref={ref} data-testid="equation-section" className="relative h-[320vh] bg-[#050505]">
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center px-6 overflow-hidden">
        <p className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-12">// THE FORMULA</p>
        <div className="flex flex-wrap items-baseline justify-center gap-x-5 gap-y-4 max-w-6xl text-center">
          {equationWords.map((w, i) => (
            <span key={w} className="flex items-baseline gap-5">
              <EquationWord word={w} index={i} total={equationWords.length} progress={scrollYProgress} />
              {i < equationWords.length - 1 && <span className="text-cyan text-xl sm:text-3xl font-light">×</span>}
            </span>
          ))}
        </div>
        <motion.div style={{ scaleX: lineScale }} className="origin-center h-px w-48 bg-cyan/60 my-10" />
        <motion.div style={{ opacity: resultOpacity, y: resultY }} className="text-center">
          <p className="font-unbounded font-extrabold uppercase tracking-tight text-3xl sm:text-5xl lg:text-6xl text-transparent bg-clip-text bg-gradient-to-r from-cyan to-white">
            COMPLETE BUSINESS SOLUTIONS
          </p>
          <p className="mt-8 max-w-2xl mx-auto text-sm md:text-base text-neutral-400 leading-relaxed">
            My extensive combination of software and hardware experience allows me to bridge the gap between physical technology and intelligent digital systems.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });
  const ringX = useTransform(sx, [-0.5, 0.5], [-36, 36]);
  const ringY = useTransform(sy, [-0.5, 0.5], [-24, 24]);
  const ringX2 = useTransform(sx, [-0.5, 0.5], [22, -22]);
  const ringY2 = useTransform(sy, [-0.5, 0.5], [16, -16]);

  const onMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMouse}
      className="relative min-h-screen flex items-center overflow-hidden bg-blueprint"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-48 -left-48 w-[640px] h-[640px] rounded-full bg-cyan/[0.07] blur-[160px]" />
        <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-cyan/[0.05] blur-[140px]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050505] to-transparent" />
      </div>

      <motion.div style={{ x: ringX, y: ringY }} className="absolute right-[-14%] top-1/2 -translate-y-1/2 hidden lg:block pointer-events-none">
        <div className="relative w-[600px] h-[600px]">
          <div className="absolute inset-0 rounded-full border border-cyan/15 animate-spin-slow" />
          <div className="absolute inset-0 rounded-full border border-dashed border-white/10 animate-spin-slower" />
          <div className="absolute inset-16 rounded-full border border-cyan/25 animate-spin-slower" />
          <div className="absolute inset-32 rounded-full border border-white/10 animate-spin-slow" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-cyan animate-pulse-glow drop-shadow-[0_0_18px_rgba(0,240,255,0.8)]" />
          </div>
          <div className="absolute top-1/2 -left-8 font-mono text-[9px] tracking-[0.3em] text-cyan/60 -translate-y-1/2">SYS.CORE</div>
          <div className="absolute top-8 left-1/2 -translate-x-1/2 font-mono text-[9px] tracking-[0.3em] text-neutral-600">ORBIT.01</div>
        </div>
      </motion.div>
      <motion.div style={{ x: ringX2, y: ringY2 }} className="absolute right-[8%] top-[18%] hidden lg:block pointer-events-none">
        <div className="border border-white/10 px-3 py-2 font-mono text-[9px] tracking-[0.3em] text-neutral-500 glass-panel">
          HW <span className="text-cyan">↔</span> SW LINK: ACTIVE
        </div>
      </motion.div>

      <div className="absolute top-24 left-6 md:left-12 font-mono text-[10px] tracking-[0.3em] text-neutral-600 hidden sm:block">
        SYS // ONLINE <span className="text-cyan animate-pulse-glow">●</span>
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 pt-36 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-mono text-[11px] tracking-[0.35em] text-cyan mb-8"
        >
          TECHNOLOGY CONSULTING — IQAISOLUTIONS.COM
        </motion.p>

        <h1 className="font-unbounded font-extrabold uppercase tracking-tight leading-[0.98] text-[12.5vw] sm:text-[9vw] lg:text-[6.6vw]">
          <MaskedLine delay={0.1}>Intelligent</MaskedLine>
          <MaskedLine delay={0.22}>Technology<span className="text-cyan">.</span></MaskedLine>
          <MaskedLine delay={0.34} className="text-outline">Practical</MaskedLine>
          <MaskedLine delay={0.46} className="text-outline">Solutions<span className="text-cyan" style={{ WebkitTextStroke: "0" }}>.</span></MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap gap-2"
        >
          {chips.map((c) => (
            <span key={c} className="border border-white/15 px-3 py-1.5 font-mono text-[9px] md:text-[10px] tracking-[0.25em] text-neutral-400">
              {c}
            </span>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 max-w-2xl space-y-4"
        >
          <p className="text-base md:text-lg text-neutral-300 leading-relaxed">
            I help businesses identify, design, prototype, and implement technology solutions that solve real-world problems.
          </p>
          <p className="text-sm md:text-base text-neutral-500 leading-relaxed">
            My experience combines software, hardware, connectivity, cloud systems, and artificial intelligence — allowing me to develop complete solutions rather than isolated pieces of technology.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <Link
            to="/services"
            data-testid="hero-cta-explore"
            className="group inline-flex items-center gap-3 bg-cyan text-black px-8 py-4 font-mono text-xs tracking-[0.25em] font-medium hover:bg-cyan-hover transition-colors duration-300"
          >
            EXPLORE SOLUTIONS
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-cta-talk"
            className="group inline-flex items-center gap-3 border border-white/25 px-8 py-4 font-mono text-xs tracking-[0.25em] text-white hover:border-cyan hover:text-cyan transition-colors duration-300"
          >
            LET'S TALK
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="font-mono text-[9px] tracking-[0.4em] text-neutral-600">SCROLL</span>
        <div className="w-px h-10 bg-gradient-to-b from-cyan/60 to-transparent" />
      </motion.div>
    </section>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <CredibilityStrip />

      <section data-testid="positioning-section" className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36">
        <ChapterTag num="01" label="Positioning" />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <MaskedLine className="font-unbounded font-semibold uppercase tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl">
              I help businesses turn
            </MaskedLine>
            <MaskedLine delay={0.12} className="font-unbounded font-semibold uppercase tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl text-cyan">
              emerging technology
            </MaskedLine>
            <MaskedLine delay={0.24} className="font-unbounded font-semibold uppercase tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl">
              into practical solutions.
            </MaskedLine>
          </div>
          <div className="md:col-span-4 flex flex-col justify-end space-y-5">
            <Reveal delay={0.2}>
              <p className="text-sm md:text-base text-neutral-400 leading-relaxed">
                Not simply an AI developer — an experienced technology consultant who understands how
                <span className="text-white"> AI + hardware + software + connectivity + cloud + data + automation </span>
                work together to solve real business problems.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link to="/about" data-testid="positioning-about-link" className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-cyan">
                READ THE FULL STORY
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="capabilities-section" className="border-t border-white/10 bg-[#070707]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36">
          <ChapterTag num="02" label="Capabilities" />
          <Reveal delay={0.1}>
            <h2 className="mt-12 font-unbounded font-semibold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl leading-[1.1]">
              One consultant. <span className="text-outline">The entire stack.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {capabilities.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06} className={c.span}>
                <Link
                  to="/services"
                  data-testid={`capability-tile-${i}`}
                  className="group block h-full bg-[#0A0A0A] p-8 md:p-10 hover:bg-[#0d1114] transition-colors duration-500 relative overflow-hidden"
                >
                  <div className="absolute inset-0 border border-transparent group-hover:border-cyan/30 transition-colors duration-500 pointer-events-none" />
                  <c.icon size={26} className="text-cyan" strokeWidth={1.5} />
                  <h3 className="mt-6 font-unbounded font-normal uppercase tracking-tight text-base md:text-lg leading-snug text-white">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-sm text-neutral-400 leading-relaxed">{c.desc}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {c.tags.map((t) => (
                      <span key={t} className="font-mono text-[9px] tracking-[0.25em] text-neutral-500 border border-white/10 px-2 py-1">
                        {t}
                      </span>
                    ))}
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <EquationSection />

      <section data-testid="difference-section" className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36">
          <ChapterTag num="07" label="Difference" />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <Reveal className="md:col-span-7">
              <h2 className="font-unbounded font-semibold uppercase tracking-tight text-3xl sm:text-5xl leading-[1.05]">
                More than<br />software<span className="text-cyan">.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="md:col-span-5">
              <p className="text-sm md:text-base text-neutral-400 leading-relaxed">
                Many technology consultants specialize in a single area. My experience spans the complete technology ecosystem — with an extensive combination of both hardware and software experience. That broader perspective makes it possible to identify solutions that might not be obvious through only one technology lens.
              </p>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {differentiators.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.05} className={d.span}>
                <div
                  data-testid={`differentiator-card-${i}`}
                  className="group h-full bg-[#0A0A0A] p-8 md:p-10 relative overflow-hidden hover:bg-[#0d1114] transition-colors duration-500"
                >
                  <div className="absolute inset-0 border border-transparent group-hover:border-cyan/30 transition-colors duration-500 pointer-events-none" />
                  <div className="flex items-start justify-between">
                    <d.icon size={24} className="text-cyan" strokeWidth={1.5} />
                    <span className="font-mono text-xs text-white/15">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  {d.sub && (
                    <p className="mt-6 font-mono text-[10px] tracking-[0.3em] text-cyan">{d.sub}</p>
                  )}
                  <h3 className={`${d.sub ? "mt-2" : "mt-6"} font-unbounded font-normal uppercase tracking-tight text-base md:text-lg text-white`}>
                    {d.title}
                  </h3>
                  <p className="mt-4 text-sm text-neutral-400 leading-relaxed">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="glass-legacy-section" className="relative border-t border-white/10 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1771189956746-e96f8bf77d42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxjaXJjdWl0JTIwYm9hcmQlMjBtaWNyb2NoaXAlMjBtYWNybyUyMGRhcmt8ZW58MHx8fHwxNzg2NjE5NzQ0fDA&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-[#050505]/40" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-28 md:py-40">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.35em] text-cyan mb-8">// EARLY. NOT LATELY.</p>
          </Reveal>
          <MaskedLine className="font-unbounded font-semibold uppercase tracking-tight leading-[1.1] text-2xl sm:text-4xl lg:text-5xl max-w-5xl">
            I've been building with emerging technology
          </MaskedLine>
          <MaskedLine delay={0.12} className="font-unbounded font-semibold uppercase tracking-tight leading-[1.1] text-2xl sm:text-4xl lg:text-5xl max-w-5xl text-cyan">
            long before AI became mainstream.
          </MaskedLine>
          <Reveal delay={0.25}>
            <p className="mt-8 max-w-2xl text-sm md:text-base text-neutral-400 leading-relaxed">
              From early Google Glass applications — dozens built for the platform at its original release — to today's AI-powered smart glasses, computer vision, connected devices, and intelligent automation.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <Link
              to="/experience"
              data-testid="glass-experience-link"
              className="group mt-10 inline-flex items-center gap-3 border border-cyan/50 px-8 py-4 font-mono text-xs tracking-[0.25em] text-cyan hover:bg-cyan hover:text-black transition-colors duration-300"
            >
              SEE THE EXPERIENCE
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="home-cta-section" className="border-t border-white/10 bg-blueprint">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-28 md:py-40">
          <ChapterTag num="09" label="Contact" />
          <MaskedLine className="mt-12 font-unbounded font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl">
            Have a problem
          </MaskedLine>
          <MaskedLine delay={0.12} className="font-unbounded font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl text-outline">
            worth solving?
          </MaskedLine>
          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-wrap gap-5">
              <Link
                to="/contact"
                data-testid="home-cta-talk"
                className="group inline-flex items-center gap-3 bg-cyan text-black px-8 py-4 font-mono text-xs tracking-[0.25em] font-medium hover:bg-cyan-hover transition-colors duration-300"
              >
                LET'S TALK
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                href="mailto:iq.ai.solutions@gmail.com"
                data-testid="home-cta-email"
                className="inline-flex items-center gap-3 border border-white/25 px-8 py-4 font-mono text-xs tracking-[0.25em] text-white hover:border-cyan hover:text-cyan transition-colors duration-300"
              >
                IQ.AI.SOLUTIONS@GMAIL.COM
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
